import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-foreground-950/25 backdrop-blur-sm'
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-18 px-4 md:px-10">
        <Link
          to="/"
          className="flex items-center gap-2 group cursor-pointer"
        >
          <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-leaf-line text-background-50 text-lg"></i>
          </span>
          <span className="font-heading text-lg font-bold whitespace-nowrap text-background-50 drop-shadow-sm">
            AllChinaBuy Codes
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className="text-sm font-semibold text-background-50 hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer drop-shadow-sm"
          >
            Home
          </Link>
          <a
            href="#categories"
            className="text-sm font-medium text-background-50/90 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer drop-shadow-sm"
          >
            Categories
          </a>
          <Link
            to="/allchinabuy-buying-guide/"
            className="text-sm font-medium text-background-50/90 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer drop-shadow-sm"
          >
            Shopping Guide
          </Link>
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium text-background-50/90 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer drop-shadow-sm"
            >
              Shop Categories
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-xl border border-background-200/80 py-2 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    onClick={() => setDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
          <a
            href="#safety"
            className="text-sm font-medium text-background-50/90 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer drop-shadow-sm"
          >
            Safety
          </a>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="text-sm font-semibold text-background-50 hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer drop-shadow-sm"
          >
            allchinabuy spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="w-9 h-9 rounded-full bg-primary-500 hover:bg-primary-600 transition-colors flex items-center justify-center cursor-pointer"
          >
            <i className="ri-arrow-right-up-line text-background-50 text-sm"></i>
          </a>
        </div>

        <button
          className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl text-background-50 drop-shadow-sm ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 flex flex-col gap-3">
          <Link to="/" className="text-sm font-semibold text-foreground-900 py-2 cursor-pointer flex items-center gap-2" onClick={() => setMenuOpen(false)}>
            <i className="ri-home-line"></i> Home
          </Link>
          <a href="#categories" className="text-sm text-foreground-700 py-2 cursor-pointer flex items-center gap-2" onClick={() => setMenuOpen(false)}>
            <i className="ri-grid-line"></i> Categories
          </a>
          <Link to="/allchinabuy-buying-guide/" className="text-sm text-foreground-700 py-2 cursor-pointer flex items-center gap-2" onClick={() => setMenuOpen(false)}>
            <i className="ri-guide-line"></i> Shopping Guide
          </Link>
          <div className="py-2">
            <p className="text-xs text-foreground-500 mb-2 font-semibold flex items-center gap-1.5">
              <i className="ri-store-2-line"></i> Shop Categories
            </p>
            <div className="grid grid-cols-2 gap-1 max-h-56 overflow-y-auto">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-xs text-foreground-700 hover:text-primary-600 py-1.5 px-2 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
                  onClick={() => setMenuOpen(false)}
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
          <a href="#safety" className="text-sm text-foreground-700 py-2 cursor-pointer flex items-center gap-2" onClick={() => setMenuOpen(false)}>
            <i className="ri-shield-check-line"></i> Safety
          </a>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mt-2"
          >
            allchinabuy spreadsheet
            <i className="ri-arrow-right-up-line"></i>
          </a>
        </div>
      )}
    </nav>
  );
}